import React, { useEffect, useRef, useState } from 'react'
import { getErrorMessage } from '@/utils/getErrorMessage'
import {
  Typography,
  Button,
  Breadcrumb,
  Layout,
  Flex,
  Card,
  Form,
  Input,
  InputNumber,
  Spin,
  message,
} from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { getPartner } from '@/api/lop/partners/getPartner'
import { createPartner } from '@/api/lop/partners/createPartner'
import { updatePartner } from '@/api/lop/partners/updatePartner'

const { Title } = Typography
const { Content } = Layout

const PartnerEditorContent: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const isEditMode = Boolean(id)

  const [loading, setLoading] = useState(isEditMode)
  const [saving, setSaving] = useState(false)
  const [partnerName, setPartnerName] = useState('')
  const [affiliateId, setAffiliateId] = useState<number | null>(null)

  const hasFetched = useRef(false)

  useEffect(() => {
    if (!id || hasFetched.current) return
    hasFetched.current = true
    ;(async () => {
      try {
        setLoading(true)
        const partner = await getPartner(id)
        if (!partner) return
        setPartnerName(partner.name)
        setAffiliateId(partner.affiliate_id ?? null)
      } catch (err) {
        message.error('Failed to load partner')
        console.error(err)
      } finally {
        setLoading(false)
      }
    })()
  }, [id])

  const handleSave = async () => {
    try {
      setSaving(true)
      const payload = { name: partnerName, affiliate_id: affiliateId! }
      if (isEditMode && id) {
        await updatePartner(id, payload)
        message.success('Partner updated')
      } else {
        const created = await createPartner(payload)
        message.success('Partner created')
        navigate(`/partners/${created.id}/edit`)
      }
    } catch (err) {
      const fallback = isEditMode
        ? 'Failed to update partner'
        : 'Failed to create partner'
      message.error(getErrorMessage(err, fallback))
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <div>
      <Flex vertical gap="middle" style={{ width: '100%' }}>
        <Breadcrumb
          items={[
            { title: 'Partners', href: '/partners' },
            { title: isEditMode ? `Edit: ${partnerName}` : 'New Partner' },
          ]}
        />

        <Flex align="center" gap="middle">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/partners')}
          />
          <Title level={3} style={{ margin: 0 }}>
            {isEditMode ? `Edit Partner: ${partnerName}` : 'Create New Partner'}
          </Title>
        </Flex>

        <Flex gap="large" style={{ marginTop: 16 }}>
          <Content style={{ flex: 1, minHeight: 400 }}>
            <Card title="Partner Info" style={{ maxWidth: 700 }}>
              <Form layout="vertical">
                <Form.Item label="Name">
                  <Input
                    value={partnerName}
                    onChange={(e) => setPartnerName(e.target.value)}
                    placeholder="Enter partner name"
                  />
                </Form.Item>
                <Form.Item label="Affiliate ID">
                  <InputNumber
                    value={affiliateId}
                    onChange={(value) => setAffiliateId(value)}
                    placeholder="Enter affiliate ID"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Form>
            </Card>
            <Flex justify="flex-end" style={{ paddingTop: 16 }}>
              <Button type="primary" onClick={handleSave} loading={saving}>
                {isEditMode ? 'Save Changes' : 'Create Partner'}
              </Button>
            </Flex>
          </Content>
        </Flex>
      </Flex>
    </div>
  )
}

export const PartnerEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  return <PartnerEditorContent key={id ?? 'new'} />
}
